import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { allDroplets } from '@editor/droplets/all-droplets';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import { editHistory } from '@/mixins/history';
import types from './site-types';

export default Vue.component('droplets-tab', {
  template: require('./template.html'),
  computed: {
    ...mapGetters([
      'dropletsTab',
      'currentSite',
      'currentPage',
      'activeDroplet',
      'siteDroplets',
      'puddleFilter',
      'editorLoader',
      'globalLoading',
    ]),
    dropletsList() {
      let loadedDroplets = this.siteDroplets;
      if (this.puddleFilter !== '') {
        loadedDroplets = _.filter(
          loadedDroplets, droplet => droplet.dropTargets.includes(this.puddleFilter),
        );
      }
      if (this.filter !== 'all' && this.searchCopy === '') {
        loadedDroplets = _.filter(loadedDroplets, droplet => droplet[this.filter]);
      }
      if (this.searchCopy !== '') {
        let input = this.search.replace(new RegExp('\\\\', 'gi'), '\\\\');
        input = new RegExp(input, 'gi');
        loadedDroplets = _.filter(loadedDroplets, droplet => droplet.label.match(input));
      }
      const limit = this.pageLimit;
      return {
        all: loadedDroplets,
        paginated: [...loadedDroplets].slice((this.pageNum - 1) * limit, this.pageNum * limit),
      };
    },
    showDynamicOption() {
      const { type, addedTypes = [] } = this.currentSite;
      const isGeneral = type === 'general';
      return !isGeneral || (isGeneral && addedTypes.length > 0);
    },
    totalPages() {
      return Math.ceil(this.dropletsList.all.length / this.pageLimit);
    },
  },
  components: {
    allDroplets,
  },
  mixins: [guid, recycleDroplet, editHistory],
  data() {
    return {
      search: '',
      searchCopy: '',
      filter: 'all',
      pageNum: 1,
      pageLimit: 10,
    };
  },
  created() {
    this.fetchDroplets();
    if (!window.drzzle.trackingListeners) {
      this.initTrackingListeners();
    }
    if (!window.drzzle.copyListeners) {
      this.initCopyListeners();
    }
  },
  mounted() {
    // Use right left arrows to swap droplets
    window.addEventListener('keydown', (event) => {
      if (this.dropletsTab) {
        if (event.keyCode === 39) {
          this.onArrowClick('next');
        }
        if (event.keyCode === 37) {
          this.onArrowClick('back');
        }
      }
    });
  },
  watch: {
    dropletsTab(open) {
      if (open) {
        this.search = '';
        this.searchCopy = '';
        this.filter = 'all';
        this.$store.dispatch('setPuddleFilter', '');
        this.pageNum = 1;
      }
    },
    search(input) {
      if (this.puddleFilter !== '') {
        this.$store.dispatch('setPuddleFilter', '');
      }
      this.filter = 'all';
      this.searchCopy = input;
    },
    puddleFilter(filter) {
      if (filter !== '') {
        this.searchCopy = '';
        this.filter = 'all';
      }
    },
    'activeDroplet._id': function check(active) {
      if (active) {
        this.$store.dispatch('toggleDroplets', false);
      }
    },
    'currentSite.addedTypes': function checkDynamic() {
      this.fetchDroplets();
    },
  },
  methods: {
    fetchDroplets() {
      const { addedTypes = [], type } = this.currentSite;
      let droplets = [];
      ['general', type].concat(addedTypes).forEach((type) => {
        droplets = droplets.concat(types[type]);
      });
      droplets = _.uniq(droplets);
      this.$store.dispatch('fetchDroplets', { droplets });
    },
    filterDroplets(type) {
      if (this.puddleFilter !== '') {
        this.$store.dispatch('setPuddleFilter', '');
      }
      this.filter = type;
      this.searchCopy = '';
      this.pageNum = 1;
    },
    toggleDroplets() {
      // Needs to be global so we can set to false when dragging items around
      this.$store.dispatch('toggleDroplets', !this.dropletsTab);
      // Auto focus search when opening droplets
      if (this.dropletsTab) {
        setTimeout(() => {
          this.$refs.searchDrps.focus();
        }, 400);
      }
    },
    shouldDisable(droplet) {
      const formsReached = droplet.name === 'form' && this._limits.forms.reached;
      return formsReached && this._published;
    },
    onPageClick(page) {
      this.pageNum = page;
    },
    onArrowClick(direction) {
      if (direction === 'back' && this.pageNum > 1) {
        this.pageNum -= 1;
      } else if (direction === 'back' && this.pageNum === 1) {
        this.pageNum = this.totalPages;
      }
      if (direction === 'next' && this.pageNum < this.totalPages) {
        this.pageNum += 1;
      } else if (direction === 'next' && this.pageNum === this.totalPages) {
        this.pageNum = 1;
      }
    },
    needsTip(droplet) {
      let message = '';
      if (this._limits.forms.reached && droplet.name === 'form' && this._published) {
        message = 'This site has reached it\'s max form limit.';
        if (this.currentSite.tier === 'Free') {
          message = `Forms are not allowed under the ${this.currentSite.tier} plan.`;
        }
      }
      return message;
    },
    initTrackingListeners() {
      // this is used for our custom tracking. we don't await these
      // as we don't care if they silently fail. They should run in
      // the background
      window.addEventListener('dropNewDroplet', (e) => {
        const payload = e.detail;
        this.$store.dispatch('setDroppedId', payload.drp._id);
        this.setEditHistory({
          type: 'new_droplet',
          meta: {
            dropletType: payload.drp.type,
          },
        });
        this._trackEvent({
          type: 'drop_new_droplet',
          meta: {
            droplet_type: payload.type,
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.currentPage.name,
            page_url: this.currentPage.url,
          },
        });
      });
      window.addEventListener('moveDroplet', (e) => {
        const payload = e.detail;
        this.$store.dispatch('setDroppedId', payload.droplet._id);
        this.setEditHistory({
          type: 'move_droplet',
          meta: {
            dropletType: payload.droplet.type,
          },
        });
        this._trackEvent({
          type: 'move_droplet',
          meta: {
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.currentPage.name,
            page_url: this.currentPage.url,
            ended_id: payload.endedId,
            started_id: payload.startedId,
            new_index: payload.newIndex,
            old_index: payload.oldIndex,
          },
        });
      });
      window.drzzle.trackingListeners = true;
    },
    initCopyListeners() {
      window.addEventListener('copyDroplet', async (e) => {
        const payload = e.detail;
        const droplet = _.cloneDeep(payload.copiedDroplet);
        const newId = this.guid();
        this.updateStyleString({
          oldId: droplet._id,
          newId,
          droplet,
        });
        droplet._id = newId;
        await this.routeRecycleAction({
          action: 'loadFromRecycled',
          newId,
          type: this._dropletType(droplet),
          droplet,
        });
        this.$store.dispatch('dropNewDroplet', {
          drp: droplet,
          list: 'droplets',
          newIndex: payload.newIndex,
          items: payload.items,
          copy: true,
        });
        this.$store.dispatch('setDroppedId', droplet._id);
        this.setEditHistory({
          type: 'copy_droplet',
          meta: {
            dropletType: droplet.type,
          },
        });
        this._trackEvent({
          type: 'copy_droplet',
          meta: {
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.currentPage.name,
            page_url: this.currentPage.url,
            copiedDroplet: payload.copiedDroplet.type,
          },
        });
      });
      window.drzzle.copyListeners = true;
    },
    async bounceContainer(payload) {
      if (payload.droplet) {
        await this.$store.dispatch('bounceDroplet', { drp: payload.droplet });
      }
      if (payload.bouncePage) {
        this.bouncePage(payload);
      }
    },
    bouncePage(payload) {
      this.$store.dispatch('setGlobalValue', {
        item: this.editorLoader,
        key: 'page',
        value: true,
      });
      this.$store.dispatch('setGlobalValue', {
        item: this.editorLoader,
        key: 'loading',
        value: true,
      });
      setTimeout(() => {
        this.$store.dispatch('setGlobalValue', {
          item: this.editorLoader,
          key: 'page',
          value: false,
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.editorLoader,
          key: 'loading',
          value: false,
        });
        setTimeout(() => {
          this._scrollToDroplet($(`#drp-${payload.newId}`));
        }, 50);
      }, 200);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/droplets-tab/index.js